import React from 'react'
import { useParams } from 'react-router-dom'
import useProjectAccess from '../../hooks/useProjectAccess'
import ProjectAccessNav from '../ProjectAccessNav'
import NoPageAccess from '../NoPageAccess'

interface ProjectAccessGuardProps {
  /** Rendered (below the access nav) only when the user may grant access. */
  children: (projectId: string) => React.ReactNode
}

/**
 * Shared route guard for the per-project access pages (`/project/:id/access/*`).
 * Resolves `:id`, waits for the project's capabilities to load, renders the
 * access sub-nav, and gates the child behind `canGrantAccess`. The two access
 * routes (`ProjectAccessUsers` / `ProjectAccessAPI`) differ only in the child,
 * which receives the resolved `projectId`.
 */
const ProjectAccessGuard = ({ children }: ProjectAccessGuardProps) => {
  const { id: projectId } = useParams<{ id: string }>() as { id: string }
  const { canGrantAccess, loaded } = useProjectAccess(projectId)

  if (!loaded) return null

  if (!canGrantAccess) return <NoPageAccess />

  return (
    <>
      <ProjectAccessNav projectId={projectId} />
      {children(projectId)}
    </>
  )
}

export default ProjectAccessGuard
